/**
 * Ambassador Popup Logic
 * Supports multiple instances on the same page without duplicate bindings.
 */

(function() {
  if (window.__ambassadorPopupInitialized) return;
  window.__ambassadorPopupInitialized = true;

  const initAmbassadorPopups = () => {
    const popups = document.querySelectorAll('.ambassador-popup-overlay');
    
    popups.forEach(popupOverlay => {
      if (popupOverlay.dataset.ambassadorBound === 'true') return;
      popupOverlay.dataset.ambassadorBound = 'true';

      const popupId = popupOverlay.id;
      const popupContainer = popupOverlay.querySelector('.qw-popup-container');
      const closeBtn = popupOverlay.querySelector('.qw-popup-close');
      const form = popupOverlay.querySelector('form');
      const submitBtn = form ? form.querySelector('.qw-btn-submit') : null;
      const msgDiv = popupOverlay.querySelector('.qw-form-message');
      
      let isSubmitting = false;

    // Open triggers are elements with href="#popup-id"
    const openBtns = document.querySelectorAll(`a[href="#${popupId}"]`);
    
    let focusableElements = [];
    let firstFocusableElement;
    let lastFocusableElement;

    const getFocusableElements = () => {
      focusableElements = popupContainer.querySelectorAll('a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])');
      firstFocusableElement = focusableElements[0];
      lastFocusableElement = focusableElements[focusableElements.length - 1];
    };

    const openPopup = (e) => {
      if (e) e.preventDefault();
      popupOverlay.classList.add('qw-is-active');
      popupOverlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      getFocusableElements();
      if (firstFocusableElement) {
        setTimeout(() => firstFocusableElement.focus(), 100);
      }
    };

    const closePopup = () => {
      if (document.activeElement && popupContainer.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      popupOverlay.classList.remove('qw-is-active');
      popupOverlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      clearErrors();
    };

    // Bind open/close events
    openBtns.forEach(btn => btn.addEventListener('click', openPopup));
    if (closeBtn) closeBtn.addEventListener('click', closePopup);
    popupOverlay.addEventListener('click', (e) => {
      if (e.target === popupOverlay) closePopup();
    });

    popupOverlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closePopup();
      
      // Focus Trap
      if (e.key === 'Tab') {
        if (e.shiftKey) { // Shift + Tab
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            e.preventDefault();
          }
        } else { // Tab
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            e.preventDefault();
          }
        }
      }
    });

    const setFieldError = (formGroup, inputEl, message) => {
      formGroup.classList.add('qw-has-error');
      let errEl = formGroup.querySelector('.qw-field-error');
      if (!errEl) {
        errEl = document.createElement('span');
        errEl.className = 'qw-field-error';
        formGroup.appendChild(errEl);
      }
      errEl.textContent = message;
    };

    const clearFieldError = (formGroup) => {
      formGroup.classList.remove('qw-has-error');
      const errEl = formGroup.querySelector('.qw-field-error');
      if (errEl) errEl.remove();
    };

    const clearErrors = () => {
      if (!form) return;
      form.querySelectorAll('.qw-form-group').forEach(group => clearFieldError(group));
      if (msgDiv) {
        msgDiv.textContent = '';
        msgDiv.className = 'qw-form-message';
        msgDiv.style.display = 'none';
      }
    };

    // Real-time input handling & restrictions
    if (form) {
      // 1. Phone number inputs: strictly allow only numbers, max 10 digits
      form.querySelectorAll('input[type="tel"], input[name*="Phone" i], input[name*="Mobile" i]').forEach(phoneInput => {
        phoneInput.setAttribute('maxlength', '10');
        phoneInput.setAttribute('inputmode', 'numeric');
        
        phoneInput.addEventListener('keydown', (e) => {
          // Allow navigation/editing keys
          if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) return;
          // Prevent non-digit keys
          if (!/^[0-9]$/.test(e.key)) {
            e.preventDefault();
          }
        });

        phoneInput.addEventListener('input', (e) => {
          // Strip any non-digit chars and truncate to 10 digits
          const cleaned = e.target.value.replace(/\D/g, '').slice(0, 10);
          if (e.target.value !== cleaned) {
            e.target.value = cleaned;
          }
          const group = e.target.closest('.qw-form-group');
          if (group) clearFieldError(group);
        });
      });

      // 2. Number inputs: prevent typing negative sign or negative values
      form.querySelectorAll('input[type="number"]').forEach(numInput => {
        numInput.setAttribute('min', '0');
        
        numInput.addEventListener('keydown', (e) => {
          if (['-', '+', 'e', 'E'].includes(e.key)) {
            e.preventDefault();
          }
        });

        numInput.addEventListener('input', (e) => {
          if (e.target.value < 0) {
            e.target.value = '';
          }
          const group = e.target.closest('.qw-form-group');
          if (group) clearFieldError(group);
        });
      });

      // 3. Clear errors for other inputs on typing
      form.addEventListener('input', (e) => {
        const group = e.target.closest('.qw-form-group');
        if (group) clearFieldError(group);
      });
      form.addEventListener('change', (e) => {
        const group = e.target.closest('.qw-form-group');
        if (group) clearFieldError(group);
      });
    }

    // Form Submission
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        if (isSubmitting) return; // Prevent duplicate clicks

        const endpoint = form.dataset.action;
        if (!endpoint || endpoint.trim() === '') {
          showMessage('Error: Google Apps Script Web App URL is missing in section settings.', 'error');
          return;
        }

        clearErrors();

        const data = {};
        let isValid = true;
        let firstInvalidInput = null;

        const formGroups = form.querySelectorAll('.qw-form-group');

        formGroups.forEach(group => {
          const input = group.querySelector('input, textarea, select');
          if (!input) return;

          const name = input.name ? input.name.trim() : '';
          const labelText = (group.querySelector('label')?.textContent || name).toLowerCase();
          const isRequired = input.hasAttribute('required');
          let val = input.value ? input.value.trim() : '';

          // 1. Radio / Checkbox Handling
          if (input.type === 'radio' || input.type === 'checkbox') {
            const checkedInputs = group.querySelectorAll('input:checked');
            if (isRequired && checkedInputs.length === 0) {
              isValid = false;
              setFieldError(group, input, 'Please select an option.');
              if (!firstInvalidInput) firstInvalidInput = input;
            } else if (checkedInputs.length > 0) {
              const checkedVals = Array.from(checkedInputs).map(i => i.value);
              data[name] = checkedVals.join(', ');
            }
            return;
          }

          // 2. Required Check
          if (isRequired && !val) {
            isValid = false;
            setFieldError(group, input, 'This field is required.');
            if (!firstInvalidInput) firstInvalidInput = input;
            return;
          }

          if (val) {
            // 3. Email Validation
            if (input.type === 'email' || labelText.includes('email')) {
              const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
              if (!emailRegex.test(val)) {
                isValid = false;
                setFieldError(group, input, 'Please enter a valid email address (e.g. name@example.com).');
                if (!firstInvalidInput) firstInvalidInput = input;
                return;
              }
            }

            // 4. Phone Number Validation (strictly 10 digits)
            if (input.type === 'tel' || labelText.includes('phone') || labelText.includes('mobile') || labelText.includes('contact')) {
              const rawDigits = val.replace(/\D/g, '');
              let phoneDigits = rawDigits;
              if (phoneDigits.length === 12 && phoneDigits.startsWith('91')) {
                phoneDigits = phoneDigits.substring(2);
              } else if (phoneDigits.length === 11 && phoneDigits.startsWith('0')) {
                phoneDigits = phoneDigits.substring(1);
              }

              if (phoneDigits.length !== 10) {
                isValid = false;
                setFieldError(group, input, 'Phone number must be exactly 10 digits.');
                if (!firstInvalidInput) firstInvalidInput = input;
                return;
              }
              val = phoneDigits; // normalize to 10 digits
            }

            // 5. Instagram Handle / Profile Validation
            if (labelText.includes('instagram') || labelText.includes('handle') || labelText.includes('insta')) {
              const instaRegex = /^(https?:\/\/(www\.)?instagram\.com\/)?@?([a-zA-Z0-9._]{1,30})\/?$/i;
              if (!instaRegex.test(val)) {
                isValid = false;
                setFieldError(group, input, 'Please enter a valid Instagram handle (e.g. @username) or profile URL.');
                if (!firstInvalidInput) firstInvalidInput = input;
                return;
              }
            }

            // 6. Non-negative Number Validation (Age, Followers, Count, etc.)
            if (input.type === 'number' || labelText.includes('age') || labelText.includes('follower') || labelText.includes('count')) {
              const numVal = Number(val);
              if (isNaN(numVal) || numVal < 0) {
                isValid = false;
                setFieldError(group, input, 'Number cannot be negative.');
                if (!firstInvalidInput) firstInvalidInput = input;
                return;
              }

              if (labelText.includes('age') && (numVal < 5 || numVal > 100)) {
                isValid = false;
                setFieldError(group, input, 'Please enter a valid age (e.g. 13 - 30).');
                if (!firstInvalidInput) firstInvalidInput = input;
                return;
              }
            }

            // 7. Textarea minimum content check
            if (input.tagName.toLowerCase() === 'textarea' && isRequired && val.length < 5) {
              isValid = false;
              setFieldError(group, input, 'Please provide a more detailed response (at least 5 characters).');
              if (!firstInvalidInput) firstInvalidInput = input;
              return;
            }

            data[name] = val;
          }
        });

        // Collect hidden fields (e.g., Form Type)
        form.querySelectorAll('input[type="hidden"]').forEach(hidden => {
          if (hidden.name && hidden.value) {
            data[hidden.name] = hidden.value;
          }
        });

        if (!isValid) {
          if (firstInvalidInput) {
            firstInvalidInput.focus();
          }
          showMessage('Please fix the errors in the form above.', 'error');
          return;
        }

        // Lock submission state
        isSubmitting = true;
        submitBtn.disabled = true;
        submitBtn.classList.add('qw-loading');
        msgDiv.style.display = 'none';

        // Submit to Google Apps Script - single direct execution
        try {
          await fetch(endpoint, {
            method: 'POST',
            mode: 'no-cors',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'text/plain;charset=utf-8'
            }
          });

          showMessage(form.dataset.successMsg || 'Success! Your application has been submitted.', 'success');
          form.reset();
          setTimeout(closePopup, 3000);
        } catch (err) {
          showMessage(form.dataset.errorMsg || 'Network error. Please check your connection.', 'error');
        } finally {
          isSubmitting = false;
          submitBtn.disabled = false;
          submitBtn.classList.remove('qw-loading');
        }
      });
    }

    function showMessage(msg, type) {
      msgDiv.textContent = msg;
      msgDiv.className = 'qw-form-message qw-' + type;
      msgDiv.style.display = 'block';
    }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAmbassadorPopups);
  } else {
    initAmbassadorPopups();
  }
})();
